/** @module i18next setup; German is the default and the fallback language. */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import de from "@/locales/de/common.json";
import en from "@/locales/en/common.json";

export const SUPPORTED_LANGUAGES = ["de", "en"] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

const STORAGE_KEY = "poke-scan.language";

function readStoredLanguage(): Language | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED_LANGUAGES.includes(stored as Language) ? (stored as Language) : null;
  } catch {
    // Private mode or blocked storage: fall back to the browser language.
    return null;
  }
}

export function detectLanguage(): Language {
  const stored = readStoredLanguage();
  if (stored) return stored;
  return navigator.language.startsWith("en") ? "en" : "de";
}

export function storeLanguage(language: Language): void {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Remembering the choice is a convenience, never a requirement.
  }
}

/** Switch the interface language and tell the document about it (screen readers read it). */
export async function switchLanguage(language: Language): Promise<void> {
  await i18n.changeLanguage(language);
  storeLanguage(language);
  document.documentElement.lang = language;
}

void i18n.use(initReactI18next).init({
  resources: { de: { common: de }, en: { common: en } },
  lng: detectLanguage(),
  fallbackLng: "de",
  defaultNS: "common",
  interpolation: { escapeValue: false },
});

export default i18n;
